// Collapsible slide navigator for section.section decks. Renders a zoomed-out
// thumbnail rail beside the doc: click a thumb to jump; in edit mode, drag to
// reorder, Add a slide (duplicate-blanked from the current one), or delete.
//
// Slides are the deck's top-level <section class="section"> elements. All ops are
// plain DOM edits on the same-origin (editable) iframe document — the editor's
// existing autosave serializes the result — so there is no new persistence path;
// onContentChanged() just tells the editor to flush. Thumbnails render each
// section inside its own same-origin iframe scoped to the deck's own styles, so
// they look like the real slide (scaled to a 16:9 box).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "./Icon";
import { useCoarsePointer, useIsSingleColumn } from "../hooks/useMediaQuery";

const SLIDE_W = 1280;
const SLIDE_H = 720;
const THUMB_W = 188;
const SCALE = THUMB_W / SLIDE_W;

interface Props {
  getDoc: () => Document | null;
  editing: boolean;
  docEpoch: number; // re-scan when the iframe (re)loads
  onContentChanged: () => void; // mark dirty + autosave after a slide op
}

let slideSeq = 0;

export function SlidePanel({ getDoc, editing, docEpoch, onContentChanged }: Props) {
  const [open, setOpen] = useState(true); // desktop rail collapse
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile bottom-sheet
  const [slides, setSlides] = useState<HTMLElement[]>([]);
  const [active, setActive] = useState(0);
  const dragFrom = useRef<number | null>(null);
  // ≤860px the desktop rail is replaced by a bottom-sheet drawer + trigger.
  const isSingleColumn = useIsSingleColumn();
  // On touch, HTML5 drag-to-reorder doesn't fire — offer ▲/▼ move buttons.
  const coarse = useCoarsePointer();

  const scan = useCallback((): HTMLElement[] => {
    const doc = getDoc();
    if (!doc) return [];
    return Array.from(doc.querySelectorAll<HTMLElement>("section.section"));
  }, [getDoc]);

  const rescan = useCallback(() => setSlides(scan()), [scan]);

  // (Re)scan slides whenever the iframe document changes.
  useEffect(() => {
    rescan();
  }, [rescan, docEpoch]);

  // Deck styles, snapshotted for the thumbnail iframes (skip our highlight style).
  const stylesHtml = useMemo(() => {
    const doc = getDoc();
    if (!doc) return "";
    return Array.from(doc.querySelectorAll("style, link[rel='stylesheet']"))
      .filter((el) => el.id !== "soapbox-hl-style")
      .map((el) => el.outerHTML)
      .join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDoc, docEpoch, slides.length]);

  const bodyClass = useMemo(() => getDoc()?.body?.className ?? "", [getDoc, docEpoch]);

  // Track which slide is in view to highlight its thumbnail. getBoundingClientRect
  // on an iframe element is relative to the IFRAME's own viewport, so we listen on
  // the iframe window and pick the section whose top is nearest the top edge.
  useEffect(() => {
    const win = getDoc()?.defaultView;
    if (!win || slides.length === 0) return;
    const onScroll = () => {
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const dist = Math.abs(s.getBoundingClientRect().top);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      setActive(best);
    };
    win.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => win.removeEventListener("scroll", onScroll);
  }, [slides, getDoc, docEpoch]);

  const jumpTo = (s: HTMLElement) =>
    s.scrollIntoView({ behavior: "smooth", block: "start" });

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const list = scan();
    const moving = list[from];
    const target = list[to];
    if (!moving || !target) return;
    if (from < to) target.after(moving);
    else target.before(moving);
    rescan();
    onContentChanged();
  };

  const addSlide = () => {
    const list = scan();
    const base = list[active] ?? list[list.length - 1];
    if (!base) return;
    const clone = base.cloneNode(true) as HTMLElement;
    // Avoid duplicate ids; give the new slide a fresh one.
    clone.removeAttribute("id");
    clone.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
    clone.setAttribute("id", `slide-${Date.now()}-${slideSeq++}`);
    clone.setAttribute("data-title", "New slide");
    // Blank the text, keeping the deck's layout/theme as a template.
    clone.querySelectorAll("h1,h2,h3,h4,h5,h6,p,li").forEach((n) => {
      n.textContent = "";
    });
    const heading = clone.querySelector("h1,h2,h3,h4,h5,h6");
    if (heading) heading.textContent = "New slide";
    base.after(clone);
    rescan();
    onContentChanged();
    requestAnimationFrame(() => jumpTo(clone));
  };

  const removeSlide = (i: number) => {
    const list = scan();
    if (list.length <= 1) return;
    if (!window.confirm("Delete this slide?")) return;
    list[i].remove();
    rescan();
    onContentChanged();
  };

  if (slides.length === 0) return null;

  const addButton = (
    <button
      type="button"
      className="btn btn-ghost tap icon-btn slide-add"
      onClick={addSlide}
      title="Add slide"
      aria-label="Add slide"
    >
      <Icon name="plus" />
    </button>
  );

  const slideList = (
    <ol className="slide-list">
      {slides.map((s, i) => (
        <li
          key={i}
          className={`slide-item${i === active ? " is-active" : ""}`}
          draggable={editing && !coarse}
          onDragStart={() => (dragFrom.current = i)}
          onDragOver={(e) => editing && !coarse && e.preventDefault()}
          onDrop={() => {
            if (dragFrom.current != null) reorder(dragFrom.current, i);
            dragFrom.current = null;
          }}
        >
          <button
            type="button"
            className="slide-thumb"
            onClick={() => {
              jumpTo(s);
              setDrawerOpen(false);
            }}
            title={s.getAttribute("data-title") ?? `Slide ${i + 1}`}
          >
            <iframe
              className="slide-thumb-frame"
              title={`Slide ${i + 1} preview`}
              tabIndex={-1}
              sandbox="allow-same-origin"
              style={{
                width: SLIDE_W,
                height: SLIDE_H,
                transform: `scale(${SCALE})`,
              }}
              srcDoc={`<!doctype html><html><head><meta charset="utf-8">${stylesHtml}<style>html,body{margin:0;overflow:hidden;background:#fff}body{width:${SLIDE_W}px;height:${SLIDE_H}px}.section{min-height:0!important;height:${SLIDE_H}px!important;overflow:hidden}</style></head><body class="${bodyClass}">${s.outerHTML}</body></html>`}
            />
          </button>
          <span className="slide-index caption num">{i + 1}</span>
          {/* Touch reorder: ▲/▼ buttons (drag doesn't fire on touch). */}
          {editing && coarse && (
            <div className="slide-move">
              <button
                type="button"
                className="btn btn-ghost tap icon-btn"
                onClick={() => reorder(i, i - 1)}
                disabled={i === 0}
                aria-label={`Move slide ${i + 1} up`}
              >
                <Icon name="chevron-up" size={16} />
              </button>
              <button
                type="button"
                className="btn btn-ghost tap icon-btn"
                onClick={() => reorder(i, i + 1)}
                disabled={i === slides.length - 1}
                aria-label={`Move slide ${i + 1} down`}
              >
                <Icon name="chevron-down" size={16} />
              </button>
            </div>
          )}
          {editing && (
            <button
              type="button"
              className="slide-del"
              onClick={() => removeSlide(i)}
              title="Delete slide"
              aria-label={`Delete slide ${i + 1}`}
            >
              <Icon name="trash" size={14} />
            </button>
          )}
        </li>
      ))}
    </ol>
  );

  // Mobile: a bottom-sheet drawer + floating trigger (the desktop rail is
  // hidden ≤860px). Thumbnails are lazy-mounted only while the drawer is open.
  if (isSingleColumn) {
    return (
      <>
        <button
          type="button"
          className="slide-fab"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open slide navigator"
          aria-expanded={drawerOpen}
        >
          <Icon name="present" size={18} />
          Slides · {slides.length}
        </button>
        {drawerOpen && (
          <div
            className="slide-drawer-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setDrawerOpen(false);
            }}
          />
        )}
        <aside
          className={`slide-drawer${drawerOpen ? " is-open" : ""}`}
          aria-label="Slides"
          aria-hidden={!drawerOpen}
        >
          <div className="slide-drawer-head">
            <span className="slide-rail-title caption">Slides · {slides.length}</span>
            {editing && addButton}
            <button
              type="button"
              className="btn btn-ghost tap icon-btn"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close slide navigator"
            >
              <Icon name="close" />
            </button>
          </div>
          {drawerOpen && slideList}
        </aside>
      </>
    );
  }

  // Desktop rail.
  return (
    <aside className={`slide-rail${open ? "" : " is-collapsed"}`} aria-label="Slides">
      <div className="slide-rail-head">
        <button
          type="button"
          className="btn btn-ghost tap icon-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Collapse slides" : "Expand slides"}
          aria-expanded={open}
        >
          <Icon name={open ? "chevron-right" : "chevron-down"} />
        </button>
        {open && <span className="slide-rail-title caption">Slides · {slides.length}</span>}
        {open && editing && addButton}
      </div>

      {open && slideList}
    </aside>
  );
}
